import _ from 'lodash'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Peer, { DataConnection, MediaConnection } from 'peerjs'
import { getEnv } from '@/utils/helpers'

type PeerjsContextType = {
  call: (connectedPeerId: string, stream: MediaStream) => void
  connectedPeers: string[]
  callConnectedPeers: (stream: MediaStream) => void
  connect: (connectingPeerId: string) => void
  disconnect: () => void
  isReady: boolean
  stream: MediaStream | null
  setStream: (stream: MediaStream | null) => void
}

type PeerjsProviderProps = {
  children?: React.ReactNode
  config?: RTCConfiguration
  isStreamer?: boolean
  onError?: (err: Error) => void
  peerId: string | null
}

// TODO: replace by env variables
const defaultPeerOption = {
  host: getEnv('VITE_PEERJS_HOST'),
  port: 443,
  path: '/medconnect',
  secure: true,
}

export const PeerjsProviderContext = createContext<PeerjsContextType | null>(null)

export function PeerjsProvider(props: PeerjsProviderProps) {
  const { children, config, isStreamer, onError, peerId } = props

  const currentStreamRef = useRef<MediaStream | null>(null)
  const dataConnectionsRef = useRef<DataConnection[]>([])
  const mediaConnectionsRef = useRef<MediaConnection[]>([])
  const peerRef = useRef<Peer | null>(null)

  const [connectedPeers, setConnectedPeers] = useState<string[]>([])
  const [currentStream, setCurrentStream] = useState<MediaStream | null>(null)
  const [isReady, setIsReady] = useState<boolean>(false)

  const peerjsOption = useMemo(
    () => ({
      ...defaultPeerOption,
      config,
    }),
    [config]
  )

  const closeDataConnections = useCallback(() => {
    const peer = peerRef.current
    if (!peer) return

    for (const dataConnection of dataConnectionsRef.current) {
      const connection = peer.connect(dataConnection.peer, { label: 'disconnect', metadata: { uid: dataConnection.metadata.uid } })
      connection.on('open', () => {
        connection.on('data', (data) => {
          if (data === 'confirm-disconnect') {
            connection.close()
            dataConnection.close()
          }
        })
        connection.send('disconnect')
      })
    }
    dataConnectionsRef.current = []
    setConnectedPeers([])
  }, [])

  const closeMediaConnections = useCallback(() => {
    for (const mediaConnection of mediaConnectionsRef.current) {
      try {
        mediaConnection.close()
      } catch (error) {
        console.log(error)
      }
    }
    mediaConnectionsRef.current = []
  }, [])

  const call = useCallback((connectedPeerId: string, stream: MediaStream) => {
    peerRef.current?.call(connectedPeerId, stream)
  }, [])

  const callConnectedPeers = useCallback(
    (stream: MediaStream) => {
      const peer = peerRef.current
      if (!peer) return

      closeMediaConnections()

      for (const dataConnection of dataConnectionsRef.current) {
        const mediaConnection = peer.call(dataConnection.peer, stream)
        mediaConnectionsRef.current.push(mediaConnection)
      }
    },
    [closeMediaConnections]
  )

  const connect = useCallback((connectingPeerId: string) => {
    const peer = peerRef.current
    if (!peer) throw new Error('No peer object is instantiated')
    const connection = peer.connect(connectingPeerId, { label: 'connect', metadata: { uid: Date.now() } })
    connection.on('open', () => {
      if (!_.includes(_.map(dataConnectionsRef.current, 'peer'), connection.peer)) {
        dataConnectionsRef.current.push(connection)
        setConnectedPeers(_.map(dataConnectionsRef.current, 'peer'))
      }
    })
  }, [])

  const setStream = useCallback((stream: MediaStream | null) => {
    setCurrentStream((prevStream) => {
      prevStream?.getTracks().forEach((track) => {
        track.stop()
      })
      return stream
    })
  }, [])

  const disconnect = useCallback(() => {
    const peer = peerRef.current
    if (!peer) return

    setStream(null)
    closeDataConnections()
    closeMediaConnections()
  }, [closeDataConnections, closeMediaConnections, setStream])

  useEffect(() => {
    return () => {
      peerRef.current?.disconnect()
      peerRef.current?.destroy()
      peerRef.current = null
    }
  }, [])

  useEffect(() => {
    currentStreamRef.current = currentStream
  }, [currentStream])

  useEffect(() => {
    if (!peerId) return

    const peer = new Peer(peerId, peerjsOption)

    peer.on('open', () => {
      setIsReady(true)
    })

    peerRef.current = peer

    return () => {
      peer.disconnect()
      peer.destroy()
      peerRef.current = null
    }
  }, [peerId, peerjsOption])

  useEffect(() => {
    const peer = peerRef.current

    if (!isReady || !peer) return

    // local peer receives call from remote peer
    peer.on('call', (mediaConnection) => {
      if (!isStreamer) {
        // because connection between pds and médecin is one-way call,
        // we dont need to answer the call with a stream
        mediaConnection.answer(new MediaStream())

        mediaConnection.on('stream', (remoteStream: MediaStream) => {
          setStream(remoteStream)
        })
      }
    })

    // others remote peers attempt to connect, local peer receives a `connection` event
    peer.on('connection', (dataConnection) => {
      dataConnection.on('open', () => {
        dataConnection.on('data', (data) => {
          if (data === 'disconnect') {
            dataConnection.send('confirm-disconnect')
            dataConnectionsRef.current = dataConnectionsRef.current.filter(
              (connection) => connection.peer !== dataConnection.peer || connection.metadata.uid !== dataConnection.metadata.uid
            )
            setConnectedPeers(_.map(dataConnectionsRef.current, 'peer'))
            if (!isStreamer) {
              setStream(null)
            }
          }
        })
      })

      if (!_.includes(_.map(dataConnectionsRef.current, 'peer'), dataConnection.peer) && dataConnection.label === 'connect') {
        dataConnectionsRef.current.push(dataConnection)
        setConnectedPeers(_.map(dataConnectionsRef.current, 'peer'))
      }

      if (currentStreamRef.current && isStreamer) peer.call(dataConnection.peer, currentStreamRef.current)
    })

    peer.on('close', () => {
      console.log('peer closed')
    })

    peer.on('disconnected', (currentId) => {
      console.log(`peer disconnected: ${currentId}`)
    })

    peer.on('error', (err: Error) => {
      console.log('peer error', err)
      onError?.(err)
    })
  }, [isReady, isStreamer, onError, setStream])

  const value = useMemo(
    () => ({
      call,
      connectedPeers,
      callConnectedPeers,
      connect,
      disconnect,
      isReady,
      stream: currentStream,
      setStream,
    }),
    [call, connectedPeers, callConnectedPeers, connect, currentStream, disconnect, isReady, setStream]
  )

  return <PeerjsProviderContext.Provider value={value}>{children}</PeerjsProviderContext.Provider>
}

export const usePeer = (): PeerjsContextType => {
  const context = useContext(PeerjsProviderContext)

  if (!context) {
    throw new Error('usePeer must be used within a PeerjsProvider')
  }

  return context
}

export default PeerjsProvider
